import React from "react";

import PrivateNotesSection from "./PrivateNotesSection";
import PublicNotesSection from "./PublicNotesSection";

import { useMeetingNotes, useUpdatePrivateMeetingNotes, useUpdatePublicMeetingNotes } from "../../../../api/hooks";
import useAutosave from "../../../../hooks/useAutosave";
import { useNotesData, useNotesState } from "../hooks";

import { config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Notes section with shared and private notes.
 * Includes autosave functionality and dropdown to switch between participants.
 */
const MeetingDetailsNotes = (): React.ReactElement => {
	const { meeting, userId, participantName, isInteractionDisabled, isAdminView, workspace } = useMeetingContext();
	const { data: notes, isLoading: isNotesLoading } = useMeetingNotes(meeting._id);

	const { mutate: updatePublicMeetingNotes, isError: isPublicNotesError } = useUpdatePublicMeetingNotes();
	const { mutate: updatePrivateMeetingNotes, isError: isPrivateNotesError } = useUpdatePrivateMeetingNotes();

	const notesData = useNotesData({
		notes,
		userId,
		meetingCreatorId: meeting?.created?.userId,
		meetingParticipants: meeting?.participants,
		participantName,
		isAdminView,
		workspace,
	});

	const {
		publicNoteContent,
		setPublicNoteContent,
		privateNoteContent,
		setPrivateNoteContent,
		showPrimaryPublicNotes,
		setShowPrimaryPublicNotes,
		isPrivateNotesVisible,
		setIsPrivateNotesVisible,
	} = useNotesState({
		notesData,
		isNotesLoading,
	});

	const isNotesDisabled = isInteractionDisabled || isNotesLoading;
	const hasPrivateNotes = Boolean(notesData.userPrivateNotes?.content?.trim());
	const isCurrentUserParticipant = meeting?.participants?.some((p) => p.userId === userId);
	const shouldShowPrivateNotes = !isAdminView || (isAdminView && isCurrentUserParticipant);

	const {
		showSaved: showPublicNotesSaved,
		showSaving: showPublicNotesSaving,
		handleChange: handlePublicNoteChange,
	} = useAutosave({
		value: publicNoteContent,
		setValue: setPublicNoteContent,
		onSave: async (content) => {
			if (notesData.primaryPublicNotes?._id) {
				updatePublicMeetingNotes({
					noteId: notesData.primaryPublicNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: Boolean(notesData.primaryPublicNotes?._id),
		minSavingTime: config.minSavingTime,
	});

	const {
		showSaved: showPrivateNotesSaved,
		showSaving: showPrivateNotesSaving,
		handleChange: handlePrivateNoteChange,
	} = useAutosave({
		value: privateNoteContent,
		setValue: setPrivateNoteContent,
		onSave: async (content) => {
			if (notesData.userPrivateNotes?._id) {
				updatePrivateMeetingNotes({
					noteId: notesData.userPrivateNotes._id,
					data: { content },
					meetingId: meeting._id,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: Boolean(notesData.userPrivateNotes?._id) && isPrivateNotesVisible,
		minSavingTime: config.minSavingTime,
	});

	const handlePublicNotesOptionSelect = (optionId: "my-notes" | "participant-notes"): void => {
		setShowPrimaryPublicNotes(optionId === "my-notes");
	};

	const handleTogglePrivateNotesVisibility = (): void => {
		setIsPrivateNotesVisible(!isPrivateNotesVisible);
	};

	return (
		<div className="flex flex-col gap-4 rounded-lg">
			<PublicNotesSection
				notesData={notesData}
				isNotesLoading={isNotesLoading}
				isNotesDisabled={isNotesDisabled}
				showPrimaryPublicNotes={showPrimaryPublicNotes}
				publicNoteContent={publicNoteContent}
				showPublicNotesSaved={showPublicNotesSaved}
				showPublicNotesSaving={showPublicNotesSaving}
				isPublicNotesError={isPublicNotesError}
				onPublicNotesOptionSelect={handlePublicNotesOptionSelect}
				onPublicNoteChange={handlePublicNoteChange}
			/>

			{shouldShowPrivateNotes && (
				<PrivateNotesSection
					isPrivateNotesVisible={isPrivateNotesVisible}
					hasPrivateNotes={hasPrivateNotes}
					isNotesLoading={isNotesLoading}
					isInteractionDisabled={isInteractionDisabled || false}
					isNotesDisabled={isNotesDisabled}
					privateNoteContent={privateNoteContent}
					showPrivateNotesSaved={showPrivateNotesSaved}
					showPrivateNotesSaving={showPrivateNotesSaving}
					isPrivateNotesError={isPrivateNotesError}
					onPrivateNoteChange={handlePrivateNoteChange}
					onToggleVisibility={handleTogglePrivateNotesVisibility}
				/>
			)}
		</div>
	);
};

export default MeetingDetailsNotes;
